/**
 * Answer validation.
 *
 * Three rules hold everywhere in this file:
 *
 * 1. Grading is **pure** — same exercise plus same response gives the same
 *    result, with no clock, no randomness and no network.
 * 2. "I cannot read this" is not "you are wrong". A blank box or `2/0` returns
 *    `malformed`, and never counts as a failed attempt against mastery.
 * 3. A wrong answer is diagnosed where possible. `incorrect` with a named
 *    misconception is the whole reason this platform exists.
 */

import { areExpressionsEquivalent, equals as fractionsEqual, identical, isParseable, isProductForm, isSimplified, numbersMatch, parseFractionExact, parseNumber, toText, type Fraction } from '@yuan-math/math';
import { misconception } from './misconceptions.ts';
import type { Exercise, GradeResult, Response } from './types.ts';

export function grade(exercise: Exercise, response: Response): GradeResult {
  const result = gradeCore(exercise, response);
  if (result.status === 'correct' || result.misconceptionId) return result;

  // Nothing authored matched — fall back to shape-based diagnosis.
  const generic = detectGenericMisconception(exercise, result.normalized);
  if (!generic) return result;
  return { ...result, misconceptionId: generic, feedback: misconception(generic)?.studentMessage ?? result.feedback };
}

function gradeCore(exercise: Exercise, response: Response): GradeResult {
  const expected = describeExpected(exercise);

  switch (exercise.answer.kind) {
    case 'choice': {
      if (response.kind !== 'choice') return malformed(expected, 'Choose an answer to continue.');
      const chosen = exercise.question.choices?.find((choice) => choice.id === response.id);
      if (response.id === exercise.answer.correctId) return correct(response.id, expected);
      const trap = chosen?.misconceptionId ?? trapFor(exercise, response.id);
      return incorrect(response.id, expected, trap);
    }

    case 'choices': {
      if (response.kind !== 'choices') return malformed(expected, 'Select every answer that applies.');
      if (response.ids.length === 0) return malformed(expected, 'Select at least one answer.');
      const chosen = [...response.ids].sort();
      const target = [...exercise.answer.correctIds].sort();
      const normalized = chosen.join(', ');
      if (chosen.length === target.length && chosen.every((id, i) => id === target[i])) {
        return correct(normalized, expected);
      }
      const wrong = chosen.find((id) => !target.includes(id));
      const trap = exercise.question.choices?.find((choice) => choice.id === wrong)?.misconceptionId;
      return incorrect(normalized, expected, trap);
    }

    case 'numeric': {
      if (response.kind !== 'text') return malformed(expected, 'Type a number.');
      const value = parseNumber(response.value);
      if (value === null) return malformed(expected, "That is not a number I can read — try digits, like 12.5.");
      const normalized = String(value);
      const tolerance = exercise.answer.tolerance ?? 1e-9;
      if (numbersMatch(value, exercise.answer.value, tolerance)) return correct(normalized, expected);
      return incorrect(normalized, expected, trapForValue(exercise, value));
    }

    case 'fraction': {
      if (response.kind !== 'text') return malformed(expected, 'Type a fraction, like 3/4.');
      const parsed = parseFractionExact(response.value);
      if (parsed === null) return malformed(expected, 'Write your answer as a fraction, like 3/4 or 1 1/2.');
      const normalized = toText(parsed);
      const spec = exercise.answer;

      const sameValue = fractionsEqual(parsed, spec.value);
      if (spec.requireExactForm) {
        if (identical(parsed, spec.value)) return correct(normalized, expected);
        if (sameValue) {
          return {
            status: 'incorrect',
            correct: false,
            normalized,
            expected,
            feedback: `${normalized} has the right value, but this question asks for a particular form.`,
          };
        }
        return incorrect(normalized, expected, trapForFraction(exercise, parsed));
      }

      if (sameValue) {
        if (spec.requireSimplified && !isSimplified(parsed)) {
          return {
            status: 'incorrect',
            correct: false,
            normalized,
            expected,
            misconceptionId: 'forgot-to-simplify',
            feedback: misconception('forgot-to-simplify')?.studentMessage ?? 'Simplify your answer.',
          };
        }
        return correct(normalized, expected);
      }
      return incorrect(normalized, expected, trapForFraction(exercise, parsed));
    }

    case 'expression': {
      if (response.kind !== 'text') return malformed(expected, 'Type an expression.');
      const text = response.value.trim();
      if (text === '') return malformed(expected, 'Type an expression to continue.');
      if (!isParseable(text)) {
        return malformed(expected, "I could not read that expression — check for a missing bracket or operator.");
      }
      if (areExpressionsEquivalent(text, exercise.answer.canonical)) {
        // Right value, wrong form — the same distinction an unsimplified
        // fraction draws, and for the same reason: here the form is the skill.
        if (exercise.answer.requireFactored && !isProductForm(text)) {
          return {
            status: 'incorrect',
            correct: false,
            normalized: text,
            expected,
            misconceptionId: 'left-unfactored',
            feedback: misconception('left-unfactored')?.studentMessage ?? 'Write your answer as a product.',
          };
        }
        return correct(text, expected);
      }
      return incorrect(text, expected, trapForExpression(exercise, text));
    }

    case 'ordering': {
      if (response.kind !== 'ordering') return malformed(expected, 'Put the items in order.');
      const normalized = response.ids.join(' < ');
      const target = exercise.answer.order;
      if (response.ids.length === target.length && response.ids.every((id, i) => id === target[i])) {
        return correct(normalized, expected);
      }
      return incorrect(normalized, expected, trapFor(exercise, normalized));
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Result constructors                                                        */
/* -------------------------------------------------------------------------- */

const correct = (normalized: string, expected: string): GradeResult => ({
  status: 'correct',
  correct: true,
  normalized,
  expected,
  feedback: 'Correct.',
});

function incorrect(normalized: string, expected: string, misconceptionId?: string): GradeResult {
  const named = misconceptionId ? misconception(misconceptionId) : undefined;
  return {
    status: 'incorrect',
    correct: false,
    normalized,
    expected,
    ...(misconceptionId ? { misconceptionId } : {}),
    feedback: named?.studentMessage ?? `Not quite. The answer is ${expected}.`,
  };
}

const malformed = (expected: string, feedback: string): GradeResult => ({
  status: 'malformed',
  correct: false,
  expected,
  feedback,
});

export function describeExpected(exercise: Exercise): string {
  const spec = exercise.answer;
  switch (spec.kind) {
    case 'choice': {
      const choice = exercise.question.choices?.find((c) => c.id === spec.correctId);
      return choice?.label ?? choice?.latex ?? spec.correctId;
    }
    case 'choices':
      return spec.correctIds
        .map((id) => exercise.question.choices?.find((c) => c.id === id))
        .map((choice, index) => choice?.label ?? choice?.latex ?? spec.correctIds[index] ?? '')
        .join(', ');
    case 'numeric':
      return String(spec.value);
    case 'fraction':
      return toText(spec.value);
    case 'expression':
      return spec.canonical;
    case 'ordering':
      return spec.order.join(' < ');
  }
}

/* -------------------------------------------------------------------------- */
/* Trap matching                                                              */
/* -------------------------------------------------------------------------- */

function trapFor(exercise: Exercise, normalized: string): string | undefined {
  return exercise.traps?.find((trap) => trap.response.trim() === normalized.trim())?.misconceptionId;
}

function trapForValue(exercise: Exercise, value: number): string | undefined {
  for (const trap of exercise.traps ?? []) {
    const trapValue = parseNumber(trap.response);
    if (trapValue !== null && numbersMatch(trapValue, value)) return trap.misconceptionId;
  }
  return undefined;
}

function trapForFraction(exercise: Exercise, value: Fraction): string | undefined {
  for (const trap of exercise.traps ?? []) {
    const trapValue = parseFractionExact(trap.response);
    if (trapValue && fractionsEqual(trapValue, value)) return trap.misconceptionId;
  }
  return undefined;
}

function trapForExpression(exercise: Exercise, text: string): string | undefined {
  for (const trap of exercise.traps ?? []) {
    if (areExpressionsEquivalent(trap.response, text)) return trap.misconceptionId;
  }
  return undefined;
}

/**
 * Diagnoses wrong answers no author anticipated, by their *shape*.
 *
 * Deliberately conservative: a false diagnosis ("you moved the decimal point")
 * is worse than none, because it teaches the student to distrust the feedback.
 */
export function detectGenericMisconception(exercise: Exercise, normalized?: string): string | undefined {
  if (!normalized) return undefined;

  if (exercise.answer.kind === 'numeric') {
    const value = parseNumber(normalized);
    if (value === null || value === 0) return undefined;
    const target = exercise.answer.value;
    if (target === 0) return undefined;
    const ratio = Math.abs(target / value);
    for (const factor of [10, 100, 1000, 0.1, 0.01, 0.001]) {
      if (numbersMatch(ratio, factor, 1e-6)) return 'place-value-shift';
    }
  }

  if (exercise.answer.kind === 'fraction') {
    const parsed = parseFractionExact(normalized);
    if (parsed && fractionsEqual(parsed, exercise.answer.value) && !isSimplified(parsed)) {
      return 'forgot-to-simplify';
    }
  }

  return undefined;
}
